/**
 * Shared widgets (screenshots/73 "Shared · everyone"; 67, 69, 70 and the S/M/L layouts in 71): Next vote, Since your
 * last visit, Forecast watch, Council tension, Upcoming meetings, Live now, Notifications, and the Number metrics.
 * Each body declares its content in priority order; smaller sizes drop from the bottom of that list (71).
 */
import { useEffect, useState } from "react";
import { useNotifications } from "../../notifications/notifications";
import type { ForecastLabel } from "../types";
import { More, OvChip, Overflow, ProjBar, Row, Stat, Tension, fit } from "./parts";
import type { WidgetProps } from "./props";

const FORECAST_CHIP: Record<ForecastLabel, { tone: "neutral" | "warning" | "info"; icon: string }> = {
  tossup: { tone: "neutral", icon: "move-horizontal" }, unlikely: { tone: "warning", icon: "trending-down" }, likely: { tone: "info", icon: "trending-up" },
};

/** Next vote (71): S date › M + your items › L + countdown and projections. */
export function NextVote({ feeds, size, group, h }: WidgetProps) {
  const v = feeds.nextVote;
  if (!v) return null;
  if (size === "S") {
    const sub = group === "resident" ? `${v.body} · ${v.yourItems} item${v.yourItems === 1 ? "" : "s"} you follow` : `${v.body} · ${v.time} · ${v.yourItems} of your items`;
    return <Stat value={v.date} sub={sub} />;
  }
  if (size === "M") {
    return (
      <div className="ov-nextvote-m">
        <div className="ov-nextvote-m__date"><span className="ov-stat__v">{v.date}</span><span className="ov-stat__sub">{v.body} · {v.time.replace(":00", "")}</span></div>
        <ul className="ov-nextvote-m__items">
          {v.items.slice(0, 2).map((i) => (
            <li key={i.recordId}><span>{i.title}</span>{i.chip && <OvChip chip={i.chip} icon="move-horizontal" />}</li>
          ))}
        </ul>
      </div>
    );
  }
  const shown = v.items.slice(0, fit(h, 63, 90));
  return (
    <div className="ov-nextvote-l">
      <div className="ov-nextvote-l__top">
        <span className="ov-stat__v ov-stat__v--l">{v.date}</span>
        <Countdown at={v.startsAt} />
      </div>
      <p className="ov-stat__sub">{v.body} · {v.dateLong} · {v.agendaCount} items · {v.yourItems} you follow</p>
      <div className="ov-rows">
        {shown.map((i) => <Row key={i.recordId} title={i.title} sub={i.projected ? `${i.projected} est.` : undefined} href={`/dashboard/legislation/${i.recordId}`}
          right={i.proj ? <ProjBar proj={i.proj} /> : undefined} />)}
      </div>
      <Overflow n={v.items.length - shown.length} />
    </div>
  );
}

/** "6 days · 02 hrs · 18 min" (71 L). Ticks once a minute. */
function Countdown({ at }: { at: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60_000); return () => clearInterval(t); }, []);
  const ms = Math.max(0, Date.parse(at) - now);
  const d = Math.floor(ms / 864e5), hr = Math.floor((ms % 864e5) / 36e5), min = Math.floor((ms % 36e5) / 6e4);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    <span className="ov-countdown" role="timer" aria-label={`${d} days, ${hr} hours, ${min} minutes`}>
      {[[String(d), "days"], [pad(hr), "hrs"], [pad(min), "min"]].map(([n, l]) => <span key={l} className="ov-countdown__cell" aria-hidden><b>{n}</b><small>{l}</small></span>)}
    </span>
  );
}

export function ForecastWatch({ feeds, h, w }: WidgetProps) {
  const items = feeds.forecastWatch?.items ?? [];
  const shown = items.slice(0, fit(h, 66));
  return (
    <div className="ov-rows ov-rows--spread">
      {shown.map((i) => {
        const c = FORECAST_CHIP[i.label];
        return (
          <Row key={i.recordId} title={i.title} sub={i.sub} href={`/dashboard/legislation/${i.recordId}`}
            right={<>
              {w >= 5 && <ProjBar proj={i.proj} />}
              <span className="ov-row__chipcol"><OvChip chip={{ text: i.text, tone: c.tone }} icon={c.icon} /></span>
            </>} />
        );
      })}
      <Overflow n={items.length - shown.length} />
    </div>
  );
}

export function SinceLastVisit({ feeds, h, size }: WidgetProps) {
  const lines = feeds.sinceLastVisit?.lines ?? [];
  const shown = lines.slice(0, size === "S" ? 2 : fit(h, 66));
  return (
    <div className="ov-rows ov-rows--spread">
      {shown.map((l, n) => (
        <Row key={n} className="ov-row--since" href={l.href} lead={<span className="ov-row__chipcol ov-row__chipcol--lead"><OvChip chip={{ text: l.tag, tone: l.tone }} /></span>} title={l.text} />
      ))}
      <Overflow n={lines.length - shown.length} />
    </div>
  );
}

export function CouncilTension({ feeds, h }: WidgetProps) {
  const topics = feeds.councilTension?.topics ?? [];
  const shown = topics.slice(0, Math.max(1, Math.floor((h * 88 - 70) / 32)));
  return (
    <div className="ov-tension-list">
      {shown.map((t) => <div key={t.topic} className="ov-tension-list__row"><span>{t.topic}</span><Tension t={t.t} /></div>)}
    </div>
  );
}

export function UpcomingMeetings({ feeds, h }: WidgetProps) {
  const ms = feeds.upcomingMeetings?.meetings ?? [];
  const shown = ms.slice(0, fit(h, 48));
  return (
    <div className="ov-rows">
      {shown.map((m) => <Row key={m.id} lead={<span className="ov-row__date">{m.date}</span>} title={m.body} sub={m.time} href={`/dashboard/meetings/${m.id}`} />)}
      <Overflow n={ms.length - shown.length} />
    </div>
  );
}

export function LiveNow({ feeds }: WidgetProps) {
  const m = feeds.liveNow?.meeting;
  if (!m) return null;
  return <div className="ov-rows"><Row title={m.body} href={`/dashboard/meetings/${m.id}`} right={<OvChip chip={{ text: "Live", tone: "positive" }} />} /></div>;
}

export function Notifications({ h }: WidgetProps) {
  const items = useNotifications((s) => s.items);
  const shown = items.slice(0, fit(h, 52));
  return (
    <div className="ov-rows">
      {shown.map((n) => <Row key={n.id} title={n.title} sub={n.detail} href={n.href} className={n.read ? undefined : "is-unread"} />)}
      <Overflow n={items.length - shown.length} />
    </div>
  );
}

/** Number widgets that show one of the overview's metrics (67 Forecast accuracy, New filings; 69 You've weighed in; 70 Lobbyists). */
export function MetricNumber({ feeds, item }: WidgetProps) {
  const m = item.config.metric ? feeds.metrics?.[item.config.metric] : undefined;
  if (!m) return null;
  return <Stat value={m.value} chip={m.chip} sub={m.sub} chipKind={item.config.metric === "lobbyistsOnItems" ? "count" : undefined} />;
}

export { More };
